import React, { useState, useEffect, useMemo } from 'react';
import config from '../config';

const STATUS_CONFIG = {
  solid: { label: 'Sólido', color: '#22c55e', bg: '#dcfce7', icon: '✓' },
  improving: { label: 'Mejorando', color: '#eab308', bg: '#fef9c3', icon: '↑' },
  weak: { label: 'Débil', color: '#ef4444', bg: '#fee2e2', icon: '↓' },
  pending: { label: 'Pendiente', color: '#94a3b8', bg: '#f1f5f9', icon: '○' }
};

function ProgressBar({ value, target = 80, height = 20, showLabel = true }) {
  const pct = Math.min(Math.max(value || 0, 0), 100);
  const color = pct >= 80 ? '#22c55e' : pct >= 60 ? '#eab308' : pct >= 30 ? '#f97316' : '#ef4444';

  return (
    <div style={{ position: 'relative', width: '100%', height, backgroundColor: '#e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{
        width: `${pct}%`, height: '100%', backgroundColor: color,
        borderRadius: 6, transition: 'width 0.5s ease'
      }} />
      {target && (
        <div style={{
          position: 'absolute', left: `${target}%`, top: 0, bottom: 0,
          width: 2, backgroundColor: '#1e293b', opacity: 0.5
        }} />
      )}
      {showLabel && (
        <span style={{
          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, fontWeight: 700, color: pct > 50 ? '#fff' : '#374151'
        }}>
          {pct != null ? `${pct}%` : '—'}
        </span>
      )}
    </div>
  );
}

function TopicCard({ topic, darkMode }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_CONFIG[topic.status] || STATUS_CONFIG.pending;
  const latestAccuracy = topic.rounds.length > 0
    ? topic.rounds[topic.rounds.length - 1].accuracy
    : topic.baseline?.accuracy;

  return (
    <div style={{
      border: `2px solid ${statusCfg.color}30`,
      borderRadius: 12, padding: 16, marginBottom: 12,
      backgroundColor: darkMode ? '#1e293b' : '#fff',
      cursor: 'pointer', transition: 'all 0.2s'
    }} onClick={() => setExpanded(!expanded)}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: '50%',
            backgroundColor: statusCfg.bg, color: statusCfg.color,
            fontWeight: 700, fontSize: 14
          }}>
            {statusCfg.icon}
          </span>
          <span style={{ fontWeight: 700, fontSize: 15, color: darkMode ? '#f1f5f9' : '#1e293b' }}>
            {topic.topic}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {topic.frequency && (
            <span style={{ fontSize: 11, color: '#64748b', backgroundColor: darkMode ? '#334155' : '#f1f5f9', padding: '2px 8px', borderRadius: 10 }}>
              {topic.frequency.years_appeared}/6 años · {topic.frequency.total_6years} preg
            </span>
          )}
          {topic.impact > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, color: topic.impact >= 10 ? '#ef4444' : topic.impact >= 5 ? '#f97316' : '#64748b' }}>
              ROI: {topic.impact}
            </span>
          )}
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 10,
            backgroundColor: statusCfg.bg, color: statusCfg.color, fontWeight: 600
          }}>
            {statusCfg.label}
          </span>
        </div>
      </div>

      {/* Progress bars */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 70 }}>Baseline:</span>
          <div style={{ flex: 1 }}>
            <ProgressBar value={topic.baseline?.accuracy} height={14} />
          </div>
        </div>
        {topic.rounds.map((round, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 70 }}>Ronda {round.round}:</span>
            <div style={{ flex: 1 }}>
              <ProgressBar value={round.accuracy} height={14} />
            </div>
            <span style={{ fontSize: 10, color: '#94a3b8', minWidth: 60 }}>
              {round.date ? new Date(round.date).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) : ''}
            </span>
          </div>
        ))}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
            <div>
              <span style={{ color: '#94a3b8' }}>Baseline RM 2025: </span>
              <span style={{ color: darkMode ? '#f1f5f9' : '#1e293b', fontWeight: 600 }}>
                {topic.baseline?.accuracy != null ? `${topic.baseline.accuracy}%` : 'Sin data'}
                {topic.baseline?.total > 0 && ` (${topic.baseline.correct}/${topic.baseline.total})`}
              </span>
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>Meta: </span>
              <span style={{ color: '#22c55e', fontWeight: 600 }}>{topic.target}%</span>
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>Frecuencia: </span>
              <span style={{ color: darkMode ? '#f1f5f9' : '#1e293b', fontWeight: 600 }}>
                {topic.frequency ? `${topic.frequency.total_6years} preg en 6 años` : 'Sin data'}
              </span>
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>Rondas: </span>
              <span style={{ color: darkMode ? '#f1f5f9' : '#1e293b', fontWeight: 600 }}>
                {topic.rounds.length} completadas
              </span>
            </div>
          </div>
          {topic.rounds.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Evolución: </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: darkMode ? '#f1f5f9' : '#1e293b' }}>
                {topic.baseline?.accuracy != null ? `${topic.baseline.accuracy}%` : '?'}
                {topic.rounds.map((r, i) => ` → ${r.accuracy}%`).join('')}
                {latestAccuracy >= 80 ? ' ✓' : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConceptMastery({ darkMode = false }) {
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaData, setAreaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = useMemo(() => config.getApiUrl(), []);

  // Load areas summary
  useEffect(() => {
    fetch(`${apiUrl}/concept-progress`)
      .then(r => r.json())
      .then(data => {
        setAreas(data.areas || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [apiUrl]);

  // Load area detail when selected
  useEffect(() => {
    if (!selectedArea) { setAreaData(null); return; }
    setLoading(true);
    fetch(`${apiUrl}/concept-progress/${encodeURIComponent(selectedArea)}`)
      .then(r => r.json())
      .then(data => {
        setAreaData(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [selectedArea, apiUrl]);

  const darkBg = darkMode ? '#0f172a' : '#f8fafc';
  const cardBg = darkMode ? '#1e293b' : '#fff';
  const textColor = darkMode ? '#f1f5f9' : '#1e293b';
  const mutedColor = '#94a3b8';

  if (error) {
    return <div style={{ padding: 20, color: '#ef4444' }}>Error: {error}</div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: textColor, margin: 0 }}>
          Dominio por Concepto
        </h2>
        <p style={{ fontSize: 13, color: mutedColor, margin: '4px 0 0' }}>
          Mide tu nivel real por tema. Baseline del RM 2025 + rondas de verificación.
        </p>
      </div>

      {/* Area selector */}
      {!selectedArea ? (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: textColor, marginBottom: 12 }}>
            Selecciona un área
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {areas.map((area, i) => (
              <div
                key={i}
                onClick={() => setSelectedArea(area.area)}
                style={{
                  backgroundColor: cardBg, borderRadius: 12, padding: 16,
                  cursor: 'pointer', border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`,
                  transition: 'transform 0.15s, box-shadow 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: textColor }}>{area.area}</span>
                  <span style={{ fontSize: 12, color: mutedColor }}>{area.total} preg</span>
                </div>
                <ProgressBar value={parseFloat(area.baseline_accuracy)} height={16} />
                <div style={{ marginTop: 6, fontSize: 11, color: mutedColor }}>
                  Baseline: {area.baseline_accuracy}% · {area.correct}/{area.total} correctas
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {/* Back button + Area header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <button
              onClick={() => setSelectedArea(null)}
              style={{
                backgroundColor: darkMode ? '#334155' : '#e5e7eb', border: 'none',
                borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                color: textColor, fontWeight: 600, fontSize: 13
              }}
            >
              ← Volver
            </button>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: textColor, margin: 0 }}>
              {selectedArea}
            </h3>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: mutedColor }}>Cargando...</div>
          ) : areaData ? (
            <div>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'Sólidos', value: areaData.summary.solid, cfg: STATUS_CONFIG.solid },
                  { label: 'Mejorando', value: areaData.summary.improving, cfg: STATUS_CONFIG.improving },
                  { label: 'Débiles', value: areaData.summary.weak, cfg: STATUS_CONFIG.weak },
                  { label: 'Pendientes', value: areaData.summary.pending, cfg: STATUS_CONFIG.pending }
                ].map((item, i) => (
                  <div key={i} style={{
                    backgroundColor: item.cfg.bg, borderRadius: 10, padding: '10px 14px',
                    textAlign: 'center', border: `1px solid ${item.cfg.color}30`
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: item.cfg.color }}>{item.value}</div>
                    <div style={{ fontSize: 11, color: item.cfg.color, fontWeight: 600 }}>{item.label}</div>
                  </div>
                ))}
              </div>

              {/* Topic cards */}
              {areaData.topics.map((topic, i) => (
                <TopicCard key={i} topic={topic} darkMode={darkMode} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}